import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useCallback, useRef, type FocusEvent, type ReactNode } from 'react';
import { Button } from './button';
import './dialog.css';

function revealFocusedField(event: FocusEvent<HTMLDivElement>) {
  const content = event.currentTarget;
  const target = event.target;
  if (content.classList.contains('dialog-full') || !content.contains(target) || target.closest('.dialog-heading, [popover]')) return;
  requestAnimationFrame(() => {
    if (!content.isConnected || document.activeElement !== target) return;
    const heading = content.querySelector<HTMLElement>(':scope > .dialog-heading');
    if (!heading) return;
    const field = target.closest('.field') ?? target;
    const hidden = heading.getBoundingClientRect().bottom + 12 - field.getBoundingClientRect().top;
    if (hidden > 0) content.scrollBy({ top: -hidden, behavior: 'instant' });
  });
}

export function Dialog({ open, onOpenChange, title, description, headerActions, children, wide = false, full = false }: {
  open: boolean; onOpenChange: (value: boolean) => void; title: string; description?: string; headerActions?: ReactNode; children: ReactNode; wide?: boolean; full?: boolean;
}) {
  const returnFocus = useRef<HTMLElement | null>(null);
  const headingRef = useCallback((heading: HTMLDivElement | null) => {
    if (!heading) return;
    const measure = () => heading.parentElement?.style.setProperty('--dialog-heading-height', `${heading.getBoundingClientRect().height}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(heading);
    return () => observer.disconnect();
  }, []);
  const closeButton = <DialogPrimitive.Close asChild><Button size="icon" variant="ghost" aria-label="Close dialog"><X size={18} /></Button></DialogPrimitive.Close>;
  return <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <DialogPrimitive.Portal><DialogPrimitive.Overlay className="dialog-overlay" />
      <DialogPrimitive.Content onFocusCapture={revealFocusedField} onOpenAutoFocus={() => { returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; }} onCloseAutoFocus={event => { if (returnFocus.current?.isConnected) { event.preventDefault(); returnFocus.current.focus(); } }} className={`dialog-content ${full ? 'dialog-full' : wide ? 'dialog-wide' : ''}`} {...(!description ? { 'aria-describedby': undefined } : {})}>
        <div className="dialog-heading" ref={headingRef}><div><DialogPrimitive.Title>{title}</DialogPrimitive.Title>{description && <DialogPrimitive.Description>{description}</DialogPrimitive.Description>}</div>
          {headerActions ? <div className="dialog-heading-actions">{headerActions}{closeButton}</div> : closeButton}
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>;
}
